import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { BaseRouteParams } from 'hocs'
import { State } from 'store'
import { Permission } from 'settings/schema'
import { hasFeature } from 'features/hocs'

export const PULSE_FEATURE = 'PulseDashboard'

// The AI claims are exact: partner_admin does NOT imply them, unlike every
// other permission (they are stored verbatim on the user, see BUIL-650).
function useHasClaim(claim: Permission, slug?: string): boolean {
    return useSelector(
        (state: State) =>
            state.auth.user?.permissions.find((p) => p.account === slug)?.permissions.includes(claim) ?? false,
    )
}

export function usePulseAccess(accountSlug?: string): boolean {
    const { accountSlug: slugFromRoute } = useParams<BaseRouteParams>()
    const slug = accountSlug ?? slugFromRoute
    const conviousAdmin = useSelector((state: State) => state.auth.user?.isAdmin ?? false)
    const features = useSelector((state: State) => state.features)
    const hasPulseClaim = useHasClaim('access_pulse', slug)

    if (conviousAdmin) {
        return true
    }
    const featureEnabled = hasFeature({ accountSlug: slug, features, featureName: PULSE_FEATURE, conviousAdmin })
    return hasPulseClaim && featureEnabled
}

export function usePulseSalesAccess(accountSlug?: string): boolean {
    const { accountSlug: slugFromRoute } = useParams<BaseRouteParams>()
    const slug = accountSlug ?? slugFromRoute
    const conviousAdmin = useSelector((state: State) => state.auth.user?.isAdmin ?? false)
    const pulseAccess = usePulseAccess(slug)
    const hasSalesClaim = useHasClaim('access_pulse_sales', slug)
    return pulseAccess && (conviousAdmin || hasSalesClaim)
}

export function useAssistantAccess(accountSlug?: string): boolean {
    const { accountSlug: slugFromRoute } = useParams<BaseRouteParams>()
    const slug = accountSlug ?? slugFromRoute
    const conviousAdmin = useSelector((state: State) => state.auth.user?.isAdmin ?? false)
    const pulseAccess = usePulseAccess(slug)
    const hasAssistantClaim = useHasClaim('access_ai_assistant', slug)
    return pulseAccess && (conviousAdmin || hasAssistantClaim)
}

export const RECOMMENDATIONS_FEATURE = 'PulseBetaRecommendations'

export function useRecommendationsAccess(accountSlug?: string): boolean {
    const { accountSlug: slugFromRoute } = useParams<BaseRouteParams>()
    const slug = accountSlug ?? slugFromRoute
    const conviousAdmin = useSelector((state: State) => state.auth.user?.isAdmin ?? false)
    const features = useSelector((state: State) => state.features)
    const assistantAccess = useAssistantAccess(slug)
    return (
        assistantAccess &&
        hasFeature({ accountSlug: slug, features, featureName: RECOMMENDATIONS_FEATURE, conviousAdmin })
    )
}
